// src/pages/RealisationDetails.jsx

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

// Imports pour Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// Imports des autres composants
import NavBarTwo from '../components/NavBarTwo';
import FooterTwo from '../components/FooterTwo';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { MdArrowBack } from 'react-icons/md';
import { Helmet } from 'react-helmet';

const RealisationDetails = () => {
  const { idRealisation } = useParams();
  const [realisation, setRealisation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRealisation = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_STRAPI_BASE_URL}/api/realisations/${idRealisation}?populate=*`
        );
        setRealisation(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error(
          'Erreur lors de la récupération de la réalisation :',
          error.message
        );
        setLoading(false);
      }
    };

    fetchRealisation();
  }, [idRealisation]);

  if (loading) {
    return <p className="text-center py-10">Chargement...</p>;
  }

  if (!realisation) {
    return <p className="text-center py-10">Réalisation non trouvée</p>;
  }

  const { nom, description, image } = realisation.attributes || {};
  const images = image?.data || [];

  return (
    <div className="bg-marronclair relative min-h-screen flex flex-col">
      <Helmet>
        <title>
          {nom
            ? `${nom} - Sarah et la pâtisserie`
            : 'Réalisation - Sarah et la pâtisserie'}
        </title>

        <meta
          name="description"
          content={
            description
              ? `${description.substring(0, 150)}...`
              : 'Découvrez les détails de cette réalisation de Sarah et la pâtisserie.'
          }
        />
      </Helmet>
      <NavBarTwo />
      <ScrollToTopButton />
      <div className="container mx-auto px-4 py-8 relative flex-grow">
        {/* --- BOUTON RETOUR EN HAUT À GAUCHE --- */}
        <div className="mb-6">
          <Link
            to="/Creations"
            className="inline-flex items-center px-4 py-2 bg-marronclair text-marronfonce font-medium shadow-sm border border-transparent hover:border-marronfonce hover:opacity-90 transition-colors rounded-md"
          >
            <MdArrowBack size={20} />
            <span className="ml-2 text-sm">Retour aux réalisations</span>
          </Link>
        </div>

        {/* --- TITRE --- */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-corinthia text-marronfonce">
            {nom || 'Nom de la réalisation'}
          </h1>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* --- LOGIQUE D’AFFICHAGE DES IMAGES --- */}
          <div className="max-w-md mx-auto w-full">
            {/* Cas 1: Plus d'une image -> Carrousel Swiper */}
            {images.length > 1 && (
              <>
                <Swiper
                  modules={[Autoplay, Pagination]}
                  pagination={{
                    clickable: true,
                    el: '.swiper-pagination-container',
                  }}
                  loop={true}
                  spaceBetween={50}
                  slidesPerView={1}
                  className="rounded-lg shadow-lg"
                >
                  {images.map((img) => (
                    <SwiperSlide key={img.id}>
                      <img
                        src={`${process.env.REACT_APP_STRAPI_BASE_URL}${
                          img.attributes.formats.large?.url ||
                          img.attributes.url
                        }`}
                        alt={`${nom} - ${img.attributes.alternativeText || ''}`}
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Conteneur personnalisé pour les points de pagination */}
                <div className="swiper-pagination-container mt-4 flex justify-center"></div>
              </>
            )}

            {/* Cas 2: Une seule image */}
            {images.length === 1 && (
              <img
                src={`${process.env.REACT_APP_STRAPI_BASE_URL}${
                  images[0].attributes.formats.large?.url ||
                  images[0].attributes.url
                }`}
                alt={nom}
                className="w-full h-auto rounded-lg shadow-lg"
              />
            )}
          </div>

          {/* --- DESCRIPTION --- */}
          <div className="flex flex-col justify-center lg:mt-0 mt-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md p-6 max-w-2xl mx-auto">
              <p className="text-base text-center lg:text-xl text-marronfonce leading-relaxed italic">
                {description || 'Aucune description disponible.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <FooterTwo />
    </div>
  );
};

export default RealisationDetails;
